/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getGroupAxisLabelsInfo } from '#PRIVATE_Axis/utils/labelUtils';
import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { Scale } from '#utils/UNSAFE_visTypes/chart/chart.types';
import {
  getLabelAt,
  getLeafEndIndex,
  getLeafStartIndex,
  GroupLevelItem,
  LevelsInfo,
  SEPARATOR_GAP
} from './hierarchicalUtils';

export function getSeparatorStartLevel(
  lastRenderedLevel: number | undefined,
  levelsInfo: Map<number, ReturnType<typeof getGroupAxisLabelsInfo>>
) {
  if (!lastRenderedLevel) {
    return 0;
  }

  let startLevel = lastRenderedLevel;

  // The start level of the separators may itself have skipped labels, but all levels after must not skip labels
  // We reset the startLevel when we find a level that skips labels
  for (let i = lastRenderedLevel - 1; i >= 0; i--) {
    const info = levelsInfo.get(i);
    if (info && info.numOfSkips > 0) {
      startLevel = i;
    }
  }

  return startLevel;
}

export function getSeparatorPaths(
  levelsArray: GroupLevelItem[][],
  separatorStartLevel: number,
  levelsInfo: LevelsInfo,
  isHoriz: boolean,
  scale: Scale,
  endOffset: number,
  isRtl: boolean
) {
  let previousLevelSize = 0;
  let x1, y1, x2, y2, x3, x4;
  const paths: [number, number, number, number][] = [];
  const offset = 0.5;

  /*
   * orientation = 'vertical'                     if rotated:
   * (x1, y1)                        (x2, y1)     (x1, y1)                 (x2, y1)
   *    |                               |            |     rotated label      |
   *    ------------- label -------------            --------------------------
   * (x1, y2)    (x3, y2)(x4, y2)    (x2, y2)     (x1, y2)                 (x2, y2)
   *
   *
   * orientation = 'horizontal'
   * (x1, y1) _______ (x2, y1)
   *         |
   *         |
   *         | label
   *         |
   *         |
   * (x1, y2) _______ (x2, y2)
   */

  for (let level = separatorStartLevel; level >= 0; level--) {
    const levelInfo = levelsInfo.get(level);

    if (!levelInfo) {
      continue;
    }

    const { maxWidth, maxHeight, tickLabels, isRotated } = levelInfo!;

    const levelSize = isHoriz ? maxHeight : maxWidth;
    if (levelSize == 0) {
      previousLevelSize = levelSize;
      continue;
    }

    // variables to keep track of whether certain edge cases apply
    // previous label exists, does not have blank name, and is within the viewport
    let prevLabelRendered = false;

    // previous label exists, but has a blank name (uneven heirarchy)
    let prevLabelEmpty = null;

    // Start drawing separators from second innermost level rendered.
    if (level < separatorStartLevel) {
      for (let i = 0; i < tickLabels.length; i++) {
        const label = tickLabels[i];
        if (label == null) continue;

        const index = label.index;

        // label exists, but has a blank name (uneven heirarchy)
        const isEmptyLabel = getLabelAt(index, level, levelsArray).length === 0;

        if (isEmptyLabel) continue;

        let isFirstLabel = label && tickLabels[index - 1] == null;
        const isLastLabel = label && tickLabels[index + 1] == null;

        const start = getLeafStartIndex(levelsArray, index, level);
        const end = getLeafEndIndex(levelsArray, index, level);

        if (isHoriz) {
          const yCoord = label.dims!.y;

          x1 = scale.transform(start - (isFirstLabel ? endOffset : offset));
          y1 = yCoord - levelSize / 2 - previousLevelSize * 0.5 - SEPARATOR_GAP;

          x2 = scale.transform(end + (isLastLabel ? endOffset : offset));
          y2 = !isRotated ? yCoord : yCoord + levelSize / 2;

          if (prevLabelRendered == false && x1 != null) {
            paths.push([x1, y2, x1, y1]);
          }

          if (x2 != null) {
            paths.push([x2, y2, x2, y1]);
          }

          // draw horizontal lines, when necessary, around non-empty labels

          let labelWidth = 0;
          if (label) labelWidth = (isRotated ? label.dims?.height : label.dims?.width)!;

          x1 = scale.transform(start - (isFirstLabel ? endOffset : offset));
          x2 = scale.transform(end + (isLastLabel ? endOffset : offset));

          x3 = label
            ? isRtl
              ? label.dims!.x + labelWidth * 0.5
              : label.dims!.x - labelWidth * 0.5
            : scale.transform(end + offset);
          x4 = label
            ? isRtl
              ? label.dims!.x - labelWidth * 0.5
              : label.dims!.x + labelWidth * 0.5
            : scale.transform(start - offset);

          if (isRotated)
            // draw horizontal line beneath rotated label
            paths.push([x1, y2!, x2, y2!]);
          else {
            // draw horizontal lines on either size of rendered label
            const spacing = isRtl ? -label.dims!.height * 0.5 : label.dims!.height * 0.5; // small space between end of horizontal lines and label
            const drawRightLine = isRtl ? x1 > x3 - spacing : x1 < x3 - spacing;
            const drawLeftLine = isRtl ? x4 + spacing > x2 : x4 + spacing < x2;

            if (drawRightLine) paths.push([x1, y2!, x3 - spacing, y2!]);

            if (drawLeftLine) paths.push([x4 + spacing, y2!, x2, y2!]);
          }
        } else {
          x1 = !isRtl
            ? label.dims!.x + SEPARATOR_GAP * 0.5
            : label.dims!.x - levelSize - SEPARATOR_GAP * 0.5;
          y1 = scale.transform(start - (isFirstLabel ? endOffset : offset));
          x2 = !isRtl
            ? label.dims!.x - levelSize - SEPARATOR_GAP * 0.5
            : label.dims!.x + SEPARATOR_GAP * 0.5;
          y2 = scale.transform(end + (isLastLabel ? endOffset : offset));

          if (
            ((!isEmptyLabel && prevLabelRendered == false) ||
              (index == 0 && isEmptyLabel && level != 0)) &&
            y1 != null
          ) {
            paths.push([x1, y1, x2, y1]);
          }

          if (y2 != null) {
            paths.push([x2, y2, x1, y2]);
          }

          // draw vertical lines, when necessary, around non-empty labels

          y1 =
            isFirstLabel && prevLabelEmpty == false
              ? 0
              : scale.transform(start - (isFirstLabel ? endOffset : offset));
          if (isFirstLabel) isFirstLabel = false;
          y2 = scale.transform(end + (isLastLabel ? endOffset : offset));

          paths.push([x2!, y1, x2!, y2]);
        }
        // information about previous label
        prevLabelRendered = !isEmptyLabel && label != null;
        prevLabelEmpty = label != null || (label == null && isEmptyLabel);
      }
    }
    previousLevelSize = levelSize; // save height or width of previous level
  }

  return paths;
}

export function getHorizontalSeparatorCoord(
  label: TextProps & {
    index: number;
  },
  scale: Scale,
  start: number,
  end: number,
  offset: number,
  levelSize: number,
  previousLevelSize: number,
  isRtl: boolean,
  isRotated: boolean,
  paths: [number, number, number, number][],
  prevLabelRendered: boolean | null
) {
  const yCoord = label.dims!.y;

  let x1 = scale.transform(start - offset);
  const y1 = !isRotated
    ? yCoord - levelSize / 2 - previousLevelSize * 0.5 - SEPARATOR_GAP
    : yCoord + previousLevelSize * 0.5;
  let x2 = scale.transform(end + offset);
  const y2 = !isRotated ? yCoord : yCoord + levelSize + previousLevelSize + 2 * SEPARATOR_GAP;

  if (prevLabelRendered == false && x1 != null) {
    paths.push([x1, y2, x1, y1]);
  }

  if (x2 != null) {
    paths.push([x2, y2, x2, y1]);
  }

  // draw horizontal lines, when necessary, around non-empty labels

  let labelWidth = 0;
  if (label) labelWidth = (isRotated ? label.dims?.height : label.dims?.width)!;

  x1 = scale.transform(start - offset);
  x2 = scale.transform(end + offset);

  const x3 = label
    ? isRtl
      ? label.dims!.x + labelWidth * 0.5
      : label.dims!.x - labelWidth * 0.5
    : scale.transform(end + offset);
  const x4 = label
    ? isRtl
      ? label.dims!.x - labelWidth * 0.5
      : label.dims!.x + labelWidth * 0.5
    : scale.transform(start - offset);

  if (isRotated)
    // draw horizontal line beneath rotated label
    paths.push([x1, y2!, x2, y2!]);
  else {
    // draw horizontal lines on either size of rendered label
    const spacing = isRtl ? -label.dims!.height * 0.5 : label.dims!.height * 0.5; // small space between end of horizontal lines and label
    const drawRightLine = isRtl ? x1 > x3 - spacing : x1 < x3 - spacing;
    const drawLeftLine = isRtl ? x4 + spacing > x2 : x4 + spacing < x2;

    if (drawRightLine) paths.push([x1, y2!, x3 - spacing, y2!]);

    if (drawLeftLine) paths.push([x4 + spacing, y2!, x2, y2!]);
  }
}
